// In-memory access token store with cookie-based refresh support.
const base_url = process.env.VUE_APP_URL;

let accessToken = null;
let refreshPromise = null;
let store = null;

// Deferred binding avoids a circular import between this module and src/store/index.js.
export function bindStore(storeInstance) {
	store = storeInstance;
}

function persist(token) {
	accessToken = token;
	if (store) {
		store.commit('setAccessToken', token);
	}
}

export function getAccessToken() {
	return accessToken;
}

export function setAccessToken(token) {
	persist(token);
}

export function clearAccessToken() {
	persist(null);
	localStorage.removeItem('user_data');
}

// Coalesces concurrent refresh attempts (e.g. boot + parallel 401s) into a single request.
export async function refreshAccessToken() {
	if (refreshPromise) {
		return refreshPromise;
	}
	refreshPromise = (async () => {
		try {
			const res = await fetch(`${base_url}/auth/refresh`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
			});
			const data = await res.json();
			if (data.access_token) {
				setAccessToken(data.access_token);
				return data.access_token;
			}
			clearAccessToken();
			return null;
		} catch (err) {
			clearAccessToken();
			return null;
		} finally {
			refreshPromise = null;
		}
	})();
	return refreshPromise;
}

// Called once per page load before the first protected navigation resolves.
export async function bootstrapAuth() {
	return refreshAccessToken();
}

// Fetch wrapper that injects the current token and retries once after a silent refresh on 401.
export async function authFetch(url, options = {}, retry = true) {
	const token = getAccessToken();
	const headers = {
		'Content-Type': 'application/json',
		...(options.headers || {}),
	};
	if (token) {
		headers.Authorization = 'Bearer ' + token;
	}
	const res = await fetch(url, { ...options, credentials: 'include', headers });
	if (res.status === 401 && retry) {
		const newToken = await refreshAccessToken();
		if (newToken) {
			return authFetch(url, options, false);
		}
	}
	return res;
}

// Invalidates the refresh cookie server-side; local state is cleared by the caller regardless of outcome.
export async function logoutRequest() {
	try {
		await fetch(`${base_url}/auth/logout`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				Authorization: 'Bearer ' + getAccessToken(),
			},
		});
	} catch (err) {
		// Network errors during logout shouldn't block clearing local session state.
	}
}
